import {
    Component,
    ElementRef,
    EventEmitter,
    Input, OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import {
    fade_in,
    fade_out,
    fade_out_absolute,
} from 'src/app/core/animations/fade';
import { HeaderPictureQuery } from '../../state/header-picture/header-picture.query';
import { InputSearchComponent } from '../input-search/input-search.component';
import { NavigationEnd, Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, filter, map, tap } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { environment } from '../../../../environments/environment';

@Component({
    standalone: false,
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    animations: [fade_out, fade_in, fade_out_absolute],
})
export class HeaderComponent implements OnInit, OnDestroy {
    @Input() size: 'default' | 'large' = 'default';
    @Input() logoUrl = '';
    @Input() showLogo = false;
    @Input() search = false;
    @Input() favourite = false;
    @Input() favouriteActive = true;
    @Input() navigationBack = false; //TODO: QUien implemente esto, tiene que tener en cuenta que si hay solo 1 clínica no debe de mostrar el botón
    @Input() border = true;
    @Input() showSkeleton = false;
    @Input() skeletonBackButton = false;
    @Input() skeletonLogo = false;
    @Input() noresponsive = 'noresponsive';
    @Input() isModal = false;
    @Input() modalVariant: 'center' | 'register' = 'center';
    @Input() modalLeadingIcon?: string;
    @Input() modalCenterTitleAction: 'close' | 'none' = 'none';
    @Input() topBarVariant?: 'home';
    @Input() searchLoading = false;
    @Input() collapsed = false;


    @Output() onClickBack = new EventEmitter<void>();
    @Output() onClickClose = new EventEmitter<void>();
    @Output() onSearch = new EventEmitter<string>();
    @Output() onFavourite = new EventEmitter<void>();
    @Output() onWriteSearch = new EventEmitter<string>();
    @Output() onShowSearch = new EventEmitter<void>();
    @Output() onHideSearch = new EventEmitter<void>();

    @ViewChild(InputSearchComponent) inputSearch: InputSearchComponent;
    @ViewChild('homeTopBar') homeTopBarRef: ElementRef<HTMLElement>;

    private readonly routesLargeLogo: string[] = ['details'];
    private readonly topBarSearchTextChanged: Subject<string> = new Subject<string>();
    searchOpen = false;
    searchValue = '';
    isSearching = false;
    searchActive = false;
    public showLargeLogo: boolean = true;
    public isNative: boolean = Capacitor.isNativePlatform();
    public isFlowwwer: boolean = !!environment.genericKey;
    public isCustom: boolean = !!environment.systemKey;
    public isBrowser: boolean = !!environment.browser;

    private router$?: Subscription;

    constructor(private headerPicturesQuery: HeaderPictureQuery, private router: Router) {}

    ngOnInit(): void {
        if (this.logoUrl === '') {
            this.headerPicturesQuery
                .select('homeCompany')
                .subscribe((homeCompany) => {
                    this.logoUrl = homeCompany;
                });
        }

        if (this.isModal) return;

        this.router$ = this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                map( (e: NavigationEnd) => e.url),
                distinctUntilChanged()
            )
            .subscribe( (path: string) => {
                const url = path.split('/').pop();
                this.showLargeLogo = this.routesLargeLogo.includes(url);
            });

        this.topBarSearchTextChanged
            .pipe(
                tap(() => (this.isSearching = true)),
                debounceTime(250),
                tap(() => (this.isSearching = false)),
                distinctUntilChanged()
            )
            .subscribe((text) => {
                if (text !== this.searchValue) {
                    return;
                }
                this.onWriteSearch.emit(text);
            });
    }

    clickBackButton(): void {
        this.onClickBack.emit();
    }

    clickCloseButton(): void {
        this.onClickClose.emit();
    }

    clickShowSearchButton(): void {
        this.searchOpen = true;
        setTimeout(() => {
            this.inputSearch.setFocus();
        }, 1);
        this.onShowSearch.emit();
    }

    clickShowHideButton() {
        this.searchOpen = false;
        this.onHideSearch.emit();
    }

    resetSearch() {
        this.searchOpen = false;
        this.searchValue = '';
        this.searchActive = false;
    }

    writeInputSearch(searchText) {
        this.onWriteSearch.emit(searchText);
    }

    searchInputSearch(searchText) {
        this.onSearch.emit(searchText);
    }

    topBarSearchFocus(): void {
        if (!this.searchActive) {
            this.searchActive = true;
            this.onShowSearch.emit();
        }
    }

    topBarSearchChange(searchText: string): void {
        this.searchValue = searchText;
        if (searchText) {
            const wasActive = this.searchActive;
            this.searchActive = true;
            if (!wasActive) {
                this.refocusTopBarSearch();
            }
            this.onShowSearch.emit();
        }
        this.topBarSearchTextChanged.next(searchText);
    }

    topBarSearchSubmit(): void {
        this.onSearch.emit(this.searchValue);
    }

    topBarSearchClear(): void {
        this.topBarSearchCancel();
    }

    topBarSearchCancel(): void {
        this.searchValue = '';
        this.isSearching = false;
        this.searchActive = false;
        this.onHideSearch.emit();
        this.onClickBack.emit();
    }

    topBarHomeBack(): void {
        this.searchValue = '';
        this.isSearching = false;
        this.searchActive = false;
        this.onHideSearch.emit();
        this.onClickBack.emit();
    }

    private refocusTopBarSearch(attemptsLeft = 20): void {
        requestAnimationFrame(() => {
            const searchEl = this.homeTopBarRef?.nativeElement?.shadowRoot?.querySelector(
                'flw-search'
            ) as (HTMLElement & { setFocus?: () => Promise<void> }) | null;

            if (searchEl?.setFocus) {
                searchEl.setFocus();
                return;
            }
            if (attemptsLeft > 0) {
                this.refocusTopBarSearch(attemptsLeft - 1);
            }
        });
    }

    clickFavourite(): void {
        this.onFavourite.emit();
    }

    ngOnDestroy() {
        this.router$?.unsubscribe();
        this.topBarSearchTextChanged.complete();
    }
}
